import { ProductIcon } from "@/components/ProductIcon";
import { HelpTooltip } from "@/components/wizard/HelpTooltip";
import type { QuestionOption } from "@/types";

interface WizardOptionProps {
  id: string;
  option: QuestionOption;
  selected: boolean;
  multiple: boolean;
  label: string;
  disabled?: boolean;
  tooltip?: string;
  tooltipRw?: string;
  tooltipMv?: string;
  showRwTooltip?: boolean;
  showMvTooltip?: boolean;
  comingSoonLabel?: string;
  onSelect: () => void;
}

function CheckIcon() {
  return (
    <svg
      className="w-2.5 h-2.5 text-ink"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function WizardOption({
  id,
  option,
  selected,
  multiple,
  label,
  disabled,
  tooltip,
  tooltipRw,
  tooltipMv,
  showRwTooltip,
  showMvTooltip,
  comingSoonLabel,
  onSelect,
}: WizardOptionProps) {
  const padding = multiple ? "px-5 py-3.5 gap-3.5" : "px-6 py-4 gap-4";

  return (
    <label
      htmlFor={`${id}-${option.value}`}
      className={[
        "text-sm font-medium leading-none cursor-pointer rounded-full border flex items-center transition-all duration-200",
        padding,
        disabled
          ? "border-ink-6 bg-transparent opacity-50 cursor-not-allowed"
          : selected
            ? "border-ink-40 bg-ink-5"
            : "border-ink-10 bg-transparent hover:border-ink-25",
      ].join(" ")}
    >
      <input
        id={`${id}-${option.value}`}
        type={multiple ? "checkbox" : "radio"}
        name={id}
        value={option.value}
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
        className="sr-only"
      />
      {multiple ? (
        <div
          className={[
            "w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors",
            selected ? "border-ink/70 bg-ink/10" : "border-ink-30",
          ].join(" ")}
          aria-hidden="true"
        >
          {selected ? <CheckIcon /> : null}
        </div>
      ) : (
        <div
          className={[
            "w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
            selected ? "border-ink/70" : "border-ink-30",
          ].join(" ")}
          aria-hidden="true"
        >
          {selected ? <div className="w-2 h-2 rounded-full bg-ink/80" /> : null}
        </div>
      )}
      {option.icons?.length ? (
        <div className="flex items-center gap-1 shrink-0">
          {option.icons.map((icon) => (
            <ProductIcon key={icon} name={icon} className="w-5 h-5 rounded shrink-0" />
          ))}
        </div>
      ) : option.icon ? (
        <ProductIcon name={option.icon} className="w-5 h-5 rounded shrink-0" />
      ) : null}
      <span
        className={[
          "font-medium transition-colors flex-grow",
          selected ? "text-foreground" : "text-foreground/70",
        ].join(" ")}
      >
        {label}
      </span>
      {option.comingSoon && comingSoonLabel ? (
        <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] font-semibold text-amber-400 tracking-wide shrink-0">
          {comingSoonLabel}
        </span>
      ) : null}
      {showRwTooltip && showMvTooltip && tooltipRw && tooltipMv ? (
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          <HelpTooltip text={tooltipRw} productIcon="rw" productLabel="Read&Write" />
          <HelpTooltip text={tooltipMv} productIcon="mindview" productLabel="MindView" />
        </div>
      ) : showRwTooltip && tooltipRw ? (
        <HelpTooltip text={tooltipRw} productIcon="rw" productLabel="Read&Write" />
      ) : showMvTooltip && tooltipMv ? (
        <HelpTooltip text={tooltipMv} productIcon="mindview" productLabel="MindView" />
      ) : tooltip ? (
        <HelpTooltip text={tooltip} side="bottom" />
      ) : null}
    </label>
  );
}
